import React, { useCallback } from "react";
import {
  type ColumnDef,
  type ColumnFiltersState,
  type VisibilityState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { InstagramIcon } from "../assets/svgs/common/instagram.svg";
import millify from "millify";
import GrowthIcon from "../assets/svgs/common/growth.svg";
import SearchIcon from "../assets/svgs/common/search.svg";
import { Input } from "./ui/input";
import { XIcon } from "lucide-react";
import { Button } from "./ui/button";
import ArrowTopRightIcon from "../assets/svgs/common/arrow-top-right.svg";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "./ui/hover-card";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";
import type { IGenreMinimal } from "../types/models/genre.models.types";
import { debounce, handleHttpError } from "../utils/utils";
import { RemoveSocialToFilterResult } from "./action-buttons/proposals/remove-social-tofilter.components";
import { AddSocialToFilterResult } from "./action-buttons/proposals/add-social-to-filter.components";
import { useToast } from "../hooks/use-toast";
import { CustomTable } from "./tables/custom-table.components";
import { Link, Outlet, useParams } from "react-router-dom";
import type { IProposalSocials } from "../hooks/http/admin/proposal-creators/add-proposal-social.admin.http";
import CommentIcon from "../assets/svgs/common/comment.svg";
import { DateTime } from "luxon";
import { useSearchProposalSocialsAdminHttp } from "../hooks/http/admin/proposal-creators/search-proposal-socials.admin.http";
import useProposalSocialSearchStore from "../store/proposal-social-search-store";
import { useUserStore } from "../store/user.store";
import PenIcon from "../assets/svgs/common/pen.svg";
import useProposalSocialStore from "../store/proposal-creators.store";

export default function ProposalSocialTable({
  data,
  fetchingSocials,
}: {
  data: IProposalSocials[];
  fetchingSocials: boolean;
}) {
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );

  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});
  const [columnPinning, setColumnPinning] = React.useState<
    import("@tanstack/react-table").ColumnPinningState
  >({
    right: ["actions"],
  });
  const { proposalId } = useParams();
  const { user } = useUserStore();

  const [search, setSearch] = React.useState("");
  const {
    data: searchData,
    setData: setSearchData,
    markCommentsRead: markCommentsReadSearch,
  } = useProposalSocialSearchStore();
  const { markCommentsRead } = useProposalSocialStore();
  const [typing, setTyping] = React.useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const columns = React.useMemo<ColumnDef<IProposalSocials>[]>(
    () => [
      {
        accessorKey: "social.handle",
        header: "User name",
        cell: ({ row }) => {
          const { social } = row.original;
          return (
            <div className="flex items-center gap-3 font-semibold tracking-wider">
              <div className="md:block hidden border-2 rounded-full w-8 h-8" />
              <a
                href={`https://instagram.com/${social.handle}/`}
                target="_blank"
                aria-label="Instagram profile link"
                rel="noreferrer"
                className="flex items-center"
              >
                <p className="w-20 md:w-fit md:max-w-32 text-ellipsis text-nowrap text-xs md:text-base underline overflow-hidden">
                  {social.handle}
                </p>
                <ArrowTopRightIcon className="w-8 h-8" />
              </a>
            </div>
          );
        },
      },

      {
        accessorKey: "proposalSocial.fixedPayout.amount",
        header: "Internal Cost",
        cell: ({ row }) => {
          const { proposalSocial } = row.original;
          return proposalSocial ? (
            <div className="flex items-center gap-2 font-semibold text-nowrap text-secondary tracking-wider">
              {(proposalSocial?.fixedPayout !== null) 
                ? `₹ ${proposalSocial.fixedPayout?.amount}`
                : "-"}
              <Link to={`${proposalSocial?.id}/internal-cost`} className="">
                <PenIcon />
              </Link>
            </div>
          ) : (
            "-"
          );
        },
      },
      {
        accessorKey: "proposalSocial.fixedPayoutAmount",
        header: "External Cost",
        cell: ({ row }) => {
          const { proposalSocial } = row.original;
          return proposalSocial ? (
            <div className="flex items-center gap-2 font-semibold text-nowrap text-secondary tracking-wider">
              {(proposalSocial?.fixedPayoutAmount !== null) 
                ? `₹ ${proposalSocial.fixedPayoutAmount?.amount}`
                : "-"}
              <Link to={`${proposalSocial?.id}/external-cost`} className="">
                <PenIcon />
              </Link>
            </div>
          ) : (
            "-"
          );
        },
      },

      {
        accessorKey: "social.followersCount",
        header: "Followers & engagement",
        cell: ({ row }) => {
          const { social } = row.original;

          return (
            <div className="flex justify-between items-center gap-4 w-fit font-semibold lowercase tracking-wider">
              <InstagramIcon className="w-5 h-5" />
              <div className="flex justify-start items-center gap-2 w-10 text-xs md:text-base">
                {millify(social.followersCount ?? 0)}
              </div>
              <div className="border-border border-l h-5" />
              <div className="flex justify-start items-center gap-2 w-fit text-xs md:text-base">
                <GrowthIcon />
                {social.engagementRate?.toFixed()}%
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: "social.genre",
        header: "Genre",
        cell: ({ row }) => {
          const { social } = row.original;
          return (
            <div className="flex flex-wrap items-center gap-2 w-64 text-white">
              {(social.genre?.filter?.length ?? 0) > 0 && (
                <Button className="flex items-start gap-2 bg-transparent h-fit font-semibold text-secondary">
                  <p className="flex flex-wrap items-center gap-2 text-white">
                    {(() => {
                      const genres: IGenreMinimal[] = Array.isArray(
                        social.genre
                      )
                        ? (social.genre as IGenreMinimal[])
                        : [];
                      const itemsToShow = genres.slice(0, 1);
                      const extraCount = genres.length - itemsToShow.length;
                      return (
                        <>
                          {itemsToShow.map((genre) => (
                            <Badge
                              key={genre.id}
                              className="flex gap-2 bg-tertiarybuttonbg border border-border capitalize"
                            >
                              <div>
                                {genre.emoji}
                                {genre.title}
                              </div>
                            </Badge>
                          ))}
                          {extraCount > 0 && (
                            <div className="flex items-center gap-2">
                              <HoverCard>
                                <HoverCardTrigger>
                                  <Badge className="flex justify-center items-center px-2 rounded-full text-black text-xs">
                                    +{extraCount} more
                                  </Badge>
                                </HoverCardTrigger>
                                <HoverCardContent
                                  align="center"
                                  className="bg-transparent backdrop-blur-lg px-0 py-4 border border-border w-full max-w-96"
                                >
                                  <div className="flex flex-col items-start gap-4 w-full text-white">
                                    <div className="flex items-start px-4 w-full">
                                      Genre
                                    </div>
                                    <Separator className="w-full" />
                                    <div className="flex flex-wrap items-center gap-2 px-4">
                                      {genres.slice(1).map((genre, index) => (
                                        <span
                                          key={genre.id}
                                          className="flex items-center gap-2 capitalize"
                                        >
                                          <div className="bg-tertiarybuttonbg px-4 py-1 border border-border rounded-full text-xs">
                                            {genre.emoji}
                                            {genre.title}
                                          </div>
                                          {index < genres.length - 3 && (
                                            <span className="text-secondary">
                                              <span className="border-border border-r h-2" />
                                            </span>
                                          )}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                </HoverCardContent>
                              </HoverCard>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </p>
                </Button>
              )}
            </div>
          );
        },
      },

      {
        accessorKey: "social.averageViews",
        header: "Average views",
        cell: ({ row }) => {
          const { social } = row.original;
          return (
            <div className="flex items-center gap-2 w-28 font-semibold text-xs md:text-base capitalize tracking-wider">
              {millify(social.averageViews ?? 0)}
            </div>
          );
        },
      },
      {
        accessorKey: "commentCount",
        header: "Comments",
        cell: ({ row }) => {
          const { proposalSocial } = row.original;
          return proposalSocial ? (
            <Link
              to={`comments/${proposalSocial?.id}`}
              onClick={() => {
                proposalSocial.commentCount > 0 &&
                !proposalSocial.commentReadByUsersList.includes(
                  user ? user.id : ""
                )
                  ? search.trim().length > 0
                    ? markCommentsReadSearch(proposalSocial.id, user?.id || "")
                    : markCommentsRead(proposalSocial.id, user?.id || "")
                  : null;
              }}
              className="flex items-center gap-2 w-28 font-semibold text-xs md:text-base capitalize tracking-wider"
            >
              <div className="relative">
                {proposalSocial.commentCount > 0 &&
                !proposalSocial.commentReadByUsersList.includes(
                  user ? user.id : ""
                ) ? (
                  <div className="top-0 right-0 absolute bg-soloboost rounded-full w-2 h-2" />
                ) : null}

                <CommentIcon />
              </div>
              {proposalSocial.commentCount}
            </Link>
          ) : (
            "-"
          );
        },
      },
      {
        accessorKey: "addedByUser",
        header: "Added by",
        cell: ({ row }) => {
          const { proposalSocial } = row.original;
          return proposalSocial ? (
            <div className="flex flex-col items-start w-28 font-semibold text-start text-xs md:text-base tracking-wider">
              {proposalSocial.addedByUser.email}
              <p className="text-[10px] text-secondary">
                {DateTime.fromISO(proposalSocial.createdAt?.toString() || "")
                  .setZone("local")
                  .toFormat("dd LLL | hh:mm a")}
              </p>
            </div>
          ) : (
            "-"
          );
        },
      },
      // {
      //   accessorKey: "averageLikes",
      //   header: "Average likes",
      //   cell: ({ row }) => {
      //     return (
      //       <div className="flex items-center gap-2 w-28 font-semibold text-xs md:text-base capitalize tracking-wider">
      //         {row.getValue("averageLikes")}
      //       </div>
      //     );
      //   },
      // },
      // {
      //   accessorKey: "averageComments",
      //   header: "Average comments",
      //   cell: ({ row }) => {
      //     return (
      //       <div className="flex items-center gap-2 w-32 font-semibold text-xs md:text-base capitalize tracking-wider">
      //         {row.getValue("averageComments")}
      //       </div>
      //     );
      //   },
      // },
      // {
      //   accessorKey: "creator",
      //   header: "Remarks",
      //   cell: () => {
      //     return (
      //       <Input
      //         type="text"
      //         placeholder="Provide your remarks..."
      //         className="pl-4 w-48 h-12 placeholder:text-secondary"
      //       />
      //     );
      //   },
      // },
      {
        id: "actions", // Change this line
        accessorKey: "id",
        header: "",
        cell: ({ row }) => {
          const { social, proposalSocial } = row.original;

          return (
            <div className="flex justify-end items-center w-40">
              <ActionButton
                proposalSocialId={proposalSocial?.id || ""}
                socialId={social.id}
                searching={search.trim().length > 0}
              />
            </div>
          );
        },
      },
    ],
    [search,]
  );

  const table = useReactTable<IProposalSocials>({
    data: search.trim().length > 0 ? searchData : data,
    columns: columns,
    getRowId: (row) =>
      row.proposalSocial ? row.proposalSocial.id : row.social.id,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onColumnPinningChange: setColumnPinning,
    initialState: {
      columnPinning: {
        right: ["actions"],
      },
    },
    enableColumnPinning: true,
    state: {
      columnFilters,
      columnVisibility,
      rowSelection,
      columnPinning,
    },
    manualPagination: true,
  });

  const { toast } = useToast();

  const {
    execute: searchSocialHttp,
    fetching: fetchingSocialsSearch,
    cancel: cancelSearchSocialHttp,
  } = useSearchProposalSocialsAdminHttp();

  const debouncedSearch = useCallback(
    debounce(async (searchValue: string) => {
      setSearchData([]);

      const searchString = searchValue.trim();
      if (!searchString) {
        return;
      }
      if (!proposalId) return;

      const response = await searchSocialHttp({
        body: null,
        queryParams: {
          query: searchString,
        },
        pathParams: { proposalId: proposalId },
      });
      if (!response.success) {
        return handleHttpError(response, toast);
      }
      setSearchData(response.data.campaignProposalSocials);
      setTyping(false);
    }, 500),
    []
  );

  function handleSearch(e: React.ChangeEvent<HTMLInputElement>) {
    cancelSearchSocialHttp();
    debouncedSearch(e.target.value);
    setSearch(e.target.value);
    setTyping(true);
    if (!e.target.value.trim().length) {
      setSearchData([]);
      document.getElementById("getProposalSocialsAdmin")?.click();
    }
  }

  return (
    <div className="flex flex-col gap-5 w-full">
      <div className="flex items-center gap-4 w-full">
        <div className="relative flex items-center bg-bginput py-2 pl-5 border border-border rounded-10 w-full">
          <SearchIcon />
          <Input
            value={search}
            onChange={(e) => {
              handleSearch(e);
            }}
            className="bg-transparent border-none font-semibold text-base placeholder:text-secondary"
            placeholder="Search by name, instagram ID, and number"
          />

          {search.trim().length > 0 && (
            <XIcon
              onClick={() => {
                setSearch("");
                setSearchData([]);
                document.getElementById("getProposalSocialsAdmin")?.click();
              }}
              className="right-5 absolute cursor-pointer"
            />
          )}
        </div>

        {/* <Link to={`${window.location.pathname}/import`}>
          <Button className="flex items-center gap-3 bg-tertiarybuttonbg px-3 py-7 border border-border rounded-xl font-semibold">
            <DocumentSmall />
            Upload CSV
          </Button>
        </Link> */}
      </div>

      {search.trim().length > 0 ? (
        <CustomTable
          table={table}
          columns={columns}
          loading={fetchingSocialsSearch || typing}
        />
      ) : (
        <CustomTable
          table={table}
          columns={columns}
          loading={fetchingSocials}
        />
      )}

      <Outlet />
    </div>
  );
}

const ActionButton = ({
  proposalSocialId,
  socialId,
  searching,
}: {
  proposalSocialId: string;
  socialId: string;
  searching: boolean;
}) => {
  if (!proposalSocialId) {
    return (
      <div className="relative flex justify-between items-center">
        <AddSocialToFilterResult id={socialId} />
      </div>
    );
  }

  return (
    <div className="relative flex justify-between items-center">
      <RemoveSocialToFilterResult searching={searching} id={proposalSocialId} />
    </div>
  );
};


